import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchDealers,
  migrateDealer,
  type DealerListItem,
  type FetchDealersParams,
} from "../lib/api";
import { useDocumentTitle } from "../hooks/use-document-title";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

function titleCase(str: string): string {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

function displayName(d: DealerListItem): string {
  const composed = `${d.firstName ?? ""} ${d.lastName ?? ""}`.trim();
  if (composed) return titleCase(composed);
  return `Dealer #${d.dealerId}`;
}

function displayOrg(d: DealerListItem): string {
  const org = (d.orgName || d.dealership || "").trim();
  return org ? titleCase(org) : "—";
}

export function DealersPage() {
  useDocumentTitle("Dealers");
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [onlyOttoEnabled, setOnlyOttoEnabled] = useState(true);
  const [showLegacy, setShowLegacy] = useState(false);
  const [migratingIds, setMigratingIds] = useState<Set<number>>(new Set());
  const [migrateErrors, setMigrateErrors] = useState<Record<number, string>>({});
  const [migrateStatus, setMigrateStatus] = useState<string | null>(null);
  const [migrateConfirmTarget, setMigrateConfirmTarget] = useState<DealerListItem | null>(null);
  const [dealers, setDealers] = useState<DealerListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const requestRef = useRef(0);

  const load = useCallback(
    async (params: FetchDealersParams, append: boolean) => {
      const requestId = ++requestRef.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const result = await fetchDealers(params);
        if (requestRef.current !== requestId) return;
        setDealers((prev) => (append ? [...prev, ...result.dealers] : result.dealers));
        setTotal(result.total);
        setHasMore(result.hasMore);
      } catch (e: unknown) {
        if (requestRef.current !== requestId) return;
        setError(e instanceof Error ? e.message : "Failed to load dealers");
      } finally {
        if (requestRef.current === requestId) {
          if (append) setLoadingMore(false);
          else setLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    void load(
      {
        search: debouncedSearch || undefined,
        onlyOttoEnabled,
        showLegacy,
        limit: PAGE_SIZE,
        offset: 0,
      },
      false,
    );
  }, [debouncedSearch, onlyOttoEnabled, showLegacy, load]);

  function loadMore() {
    void load(
      {
        search: debouncedSearch || undefined,
        onlyOttoEnabled,
        showLegacy,
        limit: PAGE_SIZE,
        offset: dealers.length,
      },
      true,
    );
  }

  async function migrate(d: DealerListItem) {
    const dealerId = d.dealerId;
    setMigratingIds((prev) => new Set(prev).add(dealerId));
    setMigrateStatus(null);
    setMigrateErrors((prev) => {
      const next = { ...prev };
      delete next[dealerId];
      return next;
    });
    try {
      const result = await migrateDealer(dealerId);
      // Migration removes seller_config.onsite, so the dealer no longer
      // matches the legacy filter - drop it locally instead of re-fetching.
      setDealers((prev) => prev.filter((x) => x.dealerId !== dealerId));
      setTotal((prev) => Math.max(prev - 1, 0));
      setMigrateStatus(
        `${displayName(d)} migrated to the new bot.${result.welcomeSent ? " Welcome message sent." : " Welcome message was not sent."}`,
      );
    } catch (err: unknown) {
      setMigrateErrors((prev) => ({
        ...prev,
        [dealerId]: err instanceof Error ? err.message : "Failed to migrate dealer",
      }));
    } finally {
      setMigratingIds((prev) => {
        const next = new Set(prev);
        next.delete(dealerId);
        return next;
      });
    }
  }

  return (
    <>
      <div className="card-header">
        <h1 className="page-title">Dealers</h1>
        {total > 0 && (
          <span style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>
            Showing {dealers.length} of {total.toLocaleString()}
          </span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          type="search"
          placeholder="Search by name, organisation, dealership or dealer ID..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{
            flex: "1 1 320px",
            minWidth: 240,
            padding: "8px 12px",
            fontSize: 14,
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text)",
          }}
        />
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: "var(--color-text-secondary)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={!onlyOttoEnabled}
            disabled={showLegacy}
            onChange={(e) => setOnlyOttoEnabled(!e.target.checked)}
          />
          Show all dealers (including bot-disabled)
        </label>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: "var(--color-text-secondary)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showLegacy}
            onChange={(e) => {
              setShowLegacy(e.target.checked);
              setMigrateStatus(null);
              setMigrateErrors({});
            }}
          />
          Show legacy bot users
        </label>
      </div>

      {loading && <div className="loading">Loading dealers...</div>}
      {error && <div className="error-msg">{error}</div>}
      {migrateStatus && (
        <div style={{ fontSize: 12, color: "var(--color-accent)", marginBottom: 8 }}>{migrateStatus}</div>
      )}

      {!loading && dealers.length === 0 && !error && (
        <div className="empty-state">
          {debouncedSearch
            ? `No dealers found matching "${debouncedSearch}"`
            : showLegacy
              ? "No legacy bot users."
              : onlyOttoEnabled
              ? "No bot-enabled dealers yet. Tick 'Show all dealers' to see everyone."
              : "No dealers."}
        </div>
      )}

      {dealers.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Dealer</th>
              <th>Organisation</th>
              <th>Last interaction</th>
              <th>Phone</th>
              <th>ID</th>
              {showLegacy && <th>Migration</th>}
            </tr>
          </thead>
          <tbody>
            {dealers.map((d) => (
              <tr
                key={d.dealerId}
                className="clickable-row"
                onClick={() => navigate(`/dealer/${d.dealerId}`)}
              >
                <td>{displayName(d)}</td>
                <td>{displayOrg(d)}</td>
                <td className="mono" style={{ color: d.lastOttoInboundAt ? undefined : "var(--color-text-soft)" }}>
                  {d.lastOttoInboundAt
                    ? new Date(d.lastOttoInboundAt).toLocaleString()
                    : "Never"}
                </td>
                <td className="mono">{d.phone || "—"}</td>
                <td className="mono">#{d.dealerId}</td>
                {showLegacy && (
                  <td onClick={(e) => e.stopPropagation()}>
                    {d.isLegacy && (
                      <button
                        className="btn btn-sm"
                        type="button"
                        onClick={() => setMigrateConfirmTarget(d)}
                        disabled={migratingIds.has(d.dealerId)}
                      >
                        {migratingIds.has(d.dealerId) ? "Migrating…" : "Migrate to new bot"}
                      </button>
                    )}
                    {migrateErrors[d.dealerId] && (
                      <div className="error-msg" style={{ fontSize: 12, marginTop: 4 }}>
                        {migrateErrors[d.dealerId]}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button
            className="btn btn-sm"
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading..." : `Load more (${total - dealers.length} remaining)`}
          </button>
        </div>
      )}

      {migrateConfirmTarget && (
        <MigrateConfirmModal
          dealerName={displayName(migrateConfirmTarget)}
          onConfirm={() => {
            const target = migrateConfirmTarget;
            setMigrateConfirmTarget(null);
            void migrate(target);
          }}
          onCancel={() => setMigrateConfirmTarget(null)}
        />
      )}
    </>
  );
}

function MigrateConfirmModal({
  dealerName,
  onConfirm,
  onCancel,
}: {
  dealerName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--color-surface-solid)",
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          maxWidth: 480,
          width: "90%",
          padding: 20,
          color: "var(--color-text)",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Migrate {dealerName} to the new bot?</h3>
        <p style={{ fontSize: 14, color: "var(--color-text-secondary)" }}>
          This turns on Otto, enables all stock notification types, turns off the old bot, and
          sends {dealerName} the Otto welcome WhatsApp message right away. This can't be undone.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-sm" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" type="button" onClick={onConfirm}>
            Migrate &amp; send welcome
          </button>
        </div>
      </div>
    </div>
  );
}
